import { useState } from 'react';

import { Drawer } from '@/components/feedback/drawer';

import {
  ACTION_FILTER_QUEUED,
  DEVICE_STATUS_FILTERS,
  SUBSCRIPTION_STATUS_FILTERS,
} from '../constants';
import { toggleFilter } from '../store';
import type { DeviceFilterGroup, DeviceFilters } from '../types';

import styles from './device-filter-panel.module.scss';

export type DeviceFilterPanelProps = {
  open: boolean;
  /** The currently *applied* filters — the draft is seeded from these each time it opens. */
  applied: DeviceFilters;
  onClose: () => void;
  onApply: (filters: DeviceFilters) => void;
};

type ChipGroupProps<G extends DeviceFilterGroup> = {
  label: string;
  group: G;
  options: { value: DeviceFilters[G][number]; label: string }[];
  draft: DeviceFilters;
  onToggle: (group: G, value: DeviceFilters[G][number]) => void;
};

function ChipGroup<G extends DeviceFilterGroup>({
  label,
  group,
  options,
  draft,
  onToggle,
}: ChipGroupProps<G>) {
  const selected = draft[group] as readonly unknown[];

  return (
    <fieldset className={styles.group}>
      <legend className={styles.groupLabel}>{label}</legend>
      <div className={styles.chips}>
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={String(option.value)}
              type="button"
              className={[styles.chip, active ? styles.chipActive : undefined]
                .filter(Boolean)
                .join(' ')}
              // Multi-select within a group, so these are toggle buttons, not radios.
              aria-pressed={active}
              onClick={() => onToggle(group, option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * The right-hand filter panel (US-112439 §10, Figma nodes 232:355909 / 232:359827).
 *
 * Selections are held as a **draft** and only handed to the caller on Apply, so Cancel (and the
 * close button, and a mask click) leave the applied filters untouched. The draft re-seeds from
 * `applied` on every open, which is what makes reopening the panel show the live filter state.
 */
export function DeviceFilterPanel({ open, applied, onClose, onApply }: DeviceFilterPanelProps) {
  const [draft, setDraft] = useState<DeviceFilters>(applied);
  const [wasOpen, setWasOpen] = useState(open);

  // Re-seed the draft from the applied filters each time the panel opens. This is React's
  // "adjust state when a prop changes" pattern (a render-phase set, not an effect): an effect
  // would render the stale draft first and then immediately re-render.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(applied);
  }

  const handleToggle = <G extends DeviceFilterGroup>(group: G, value: DeviceFilters[G][number]) => {
    setDraft((current) => toggleFilter(current, group, value));
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      primaryVariant="neutral"
      onPrimary={() => onApply(draft)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <div className={styles.panel}>
        <ChipGroup
          label="Device Status"
          group="status"
          options={DEVICE_STATUS_FILTERS}
          draft={draft}
          onToggle={handleToggle}
        />
        <ChipGroup
          label="Action"
          group="action"
          options={[{ value: ACTION_FILTER_QUEUED, label: 'Queued' }]}
          draft={draft}
          onToggle={handleToggle}
        />
        <ChipGroup
          label="Subscription Status"
          group="subscriptionStatus"
          options={SUBSCRIPTION_STATUS_FILTERS}
          draft={draft}
          onToggle={handleToggle}
        />
      </div>
    </Drawer>
  );
}
